/*
 * -----------------------------------------------------------------------
 *  <copyright file="event-log/search.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'hooks';
import { SaveButton } from 'views/components/core/buttons';
import DatePicker from 'views/components/core/date-picker';
import TextField from 'views/components/core/text-field';
import 'views/pages/admin/admin.scss';
import { SearchEventLogParams, SearchEventLogsFunction } from './index';
import './event-log.scss';

interface EventLogSearchInterface {
    onSearch: SearchEventLogsFunction;
    searchParams: SearchEventLogParams;
    setSearchParams: Dispatch<SetStateAction<SearchEventLogParams>>;
    clearSearch: () => void;
}

const EventLogSearch = ({ onSearch, searchParams, setSearchParams, clearSearch }: EventLogSearchInterface) => {
    const { t } = useTranslation();

    const onChange = (name: string) => (event: any) => {
        const { value } = event.target;

        setSearchParams({
            ...searchParams,
            [name]: value,
        });
    };

    return (
        <div className="log-wrap">
            <div className="log-fields-group-wrap">
                <span className="date-label">{t('admin:between')}:</span>
                <div id="log-start-date" className="date-field-wrap">
                    <DatePicker
                        label="Start Date"
                        customId="start-date"
                        name="startDate"
                        labelWidth={135}
                        selectedDate={searchParams.startDate}
                        handleDateChange={onChange('startDate')}
                    />
                </div>
                <span className="date-label">{t('admin:and')}</span>
                <div id="log-end-date" className="date-field-wrap">
                    <DatePicker
                        label="End Date"
                        customId="end-date"
                        name="endDate"
                        labelWidth={135}
                        selectedDate={searchParams.endDate}
                        handleDateChange={onChange('endDate')}
                    />
                </div>
            </div>
            <div className="log-fields-group-wrap">
                <span className="date-label">{t('eventLog:user')}:</span>
                <TextField
                    id="event-log-user-email"
                    className="event-log-email-field"
                    name="userEmail"
                    onChange={onChange('userEmail')}
                    label="Email"
                    variant="outlined"
                    value={searchParams.userEmail}
                />
                <span className="date-label">{t('eventLog:actionedBy')}:</span>
                <TextField
                    id="event-log-actioned-by-email"
                    className="event-log-email-field"
                    name="actionedByEmail"
                    onChange={onChange('actionedByEmail')}
                    label="Email"
                    variant="outlined"
                    value={searchParams.actionedByEmail}
                />
            </div>
            <div className="search-button-wrap">
                <SaveButton
                    text={t('common:clear')}
                    onClick={() => {
                        clearSearch();
                    }}
                    className="mr"
                />
                <SaveButton
                    text={t('common:search')}
                    onClick={() => {
                        onSearch(searchParams);
                    }}
                />
            </div>
        </div>
    );
};

export default EventLogSearch;
